import { isTrialTariff, PAYMENT_PRODUCTS, PRODUCT_KEYS } from '@focuslee/shared';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { type FC } from 'react';
import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx } from '../helpers/liquidGlassSurface';
import { TELEGRAM_STAR_COLOR } from '../helpers/telegramStarColor';
import { CommonIcon } from './CommonIcon';

const PREMIUM_PRODUCT = PAYMENT_PRODUCTS[PRODUCT_KEYS.premiumMonthly];
const SECONDS_PER_DAY = 24 * 60 * 60;

export interface PremiumUpsellCardProps {
    primaryApp?: 'focuslee' | 'healthlee';
    role: string;
    tariff?: string | null;
    subscriptionExpiresAt?: number | null;
    renewsAutomatically: boolean;
    locale: string;
    onClick: () => void;
    accentColor: string;
    title: string;
    activeTitle: string;
    pricePeriodLabel: string;
    focusLeeFeaturesText: string;
    healthLeeFeaturesText: string;
    startTrialDescription: string;
    subscribeDescription: string;
    trialActiveDescription: (daysLeft: number) => string;
    subscriptionActiveDescription: (nextChargeDate: string) => string;
    premiumExpiresDescription: (date: string) => string;
    startTrialButtonLabel: string;
    subscribeButtonLabel: string;
}

export const PremiumUpsellCard: FC<PremiumUpsellCardProps> = ({
    primaryApp = 'focuslee',
    role,
    tariff,
    subscriptionExpiresAt,
    renewsAutomatically,
    locale,
    onClick,
    accentColor,
    title,
    activeTitle,
    pricePeriodLabel,
    focusLeeFeaturesText,
    healthLeeFeaturesText,
    startTrialDescription,
    subscribeDescription,
    trialActiveDescription,
    subscriptionActiveDescription,
    premiumExpiresDescription,
    startTrialButtonLabel,
    subscribeButtonLabel,
}) => {
    const isPremiumActive = role === 'Premium' || role === 'Admin';
    const isTrialActive = isPremiumActive && isTrialTariff(tariff);
    const hasUsedTrial = Boolean(tariff);
    const daysLeft =
        isTrialActive && subscriptionExpiresAt
            ? Math.max(0, Math.ceil((subscriptionExpiresAt - Math.floor(Date.now() / 1000)) / SECONDS_PER_DAY))
            : 0;

    const description = hasUsedTrial ? subscribeDescription : startTrialDescription;
    const buttonLabel = hasUsedTrial ? subscribeButtonLabel : startTrialButtonLabel;
    const expiryDate =
        isPremiumActive && !isTrialActive && subscriptionExpiresAt
            ? new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', year: 'numeric' }).format(subscriptionExpiresAt * 1000)
            : null;
    const activeDescription = isTrialActive
        ? trialActiveDescription(daysLeft)
        : expiryDate
        ? renewsAutomatically
            ? subscriptionActiveDescription(expiryDate)
            : premiumExpiresDescription(expiryDate)
        : hasUsedTrial
        ? subscribeDescription
        : null;

    return (
        <Box sx={{ borderRadius: '12px', overflow: 'visible', ...liquidGlassRowShadowSx }}>
            <Stack
                component="button"
                type="button"
                onClick={onClick}
                spacing={1}
                sx={{
                    p: 1.75,
                    m: 0,
                    width: '100%',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    ...liquidGlassSurfaceFillSx,
                    border: `1.5px solid ${accentColor}`,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CommonIcon icon="workspace_premium" type="filled" size={18} color={accentColor} />
                        <Text style={{ fontSize: 13, fontWeight: 700 }}>{isPremiumActive ? activeTitle : title}</Text>
                    </Stack>
                    {isPremiumActive ? (
                        <CommonIcon icon="chevron_right" type="filled" size={20} color="var(--tg-theme-hint-color)" />
                    ) : (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CommonIcon icon="star" type="filled" size={13} color={TELEGRAM_STAR_COLOR} />
                            <Text style={{ fontSize: 14, fontWeight: 700 }}>
                                {PREMIUM_PRODUCT.amount} {pricePeriodLabel}
                            </Text>
                        </Stack>
                    )}
                </Stack>

                <Stack spacing={0.25}>
                    <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                        <CommonIcon icon="check" type="filled" size={13} color={accentColor} />
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.4 }}>
                            {primaryApp === 'healthlee' ? healthLeeFeaturesText : focusLeeFeaturesText}
                        </Caption>
                    </Stack>
                    <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                        <CommonIcon icon="check" type="filled" size={13} color={accentColor} />
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.4 }}>
                            {primaryApp === 'healthlee' ? focusLeeFeaturesText : healthLeeFeaturesText}
                        </Caption>
                    </Stack>
                </Stack>

                {isPremiumActive ? (
                    activeDescription && (
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.5 }}>
                            {activeDescription}
                        </Caption>
                    )
                ) : (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 11, lineHeight: 1.5, flex: 1, minWidth: 0 }}>
                            {description}
                        </Caption>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '999px',
                                bgcolor: accentColor,
                                color: '#fff',
                            }}
                        >
                            <Text style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{buttonLabel}</Text>
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};
